import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { knowledge } from '@cwos/api-client';
import { Button, Pill, SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { LoadFailure, loadFailureKind } from '../LoadFailure.js';
import { formatInstantDay } from './libraryLogic.js';
import {
  ackState,
  articleStatusLabel,
  articleStatusSentence,
  articleStatusVariant,
  audienceSentence,
  ledgerState,
  unpublishedDraftNote,
  versionStanding,
  versionStandingLabel,
} from './articlesLogic.js';

/**
 * ONE WRITTEN POLICY — read it, acknowledge it, revise it, publish it, and see
 * who has signed.
 *
 * ===========================================================================
 * WHY THIS SCREEN DID NOT EXIST
 * ===========================================================================
 * Fourteen routes, a complete typed client, and `grep` found ZERO
 * `knowledge.<method>` calls in either app. Ask Phibian could already read
 * these articles through its `knowledge.list` / `knowledge.get` tools — so the
 * model could see the Library and the customer could not. This is the customer
 * half.
 *
 * ===========================================================================
 * THE BODY IS UNTRUSTED DATA AND IS RENDERED AS TEXT
 * ===========================================================================
 * A policy body is text that was typed by a person, pasted out of a handbook,
 * or — once ingestion is wired end to end — lifted from a document somebody
 * uploaded. It is DATA. It is rendered through React's own escaping into a
 * `<p>`, with `white-space: pre-wrap` doing the paragraph work, and there is no
 * markdown pass, no linkifier, no sanitiser and above all no
 * `dangerouslySetInnerHTML` anywhere in this feature — `library-untrusted-body`
 * asserts that as a property of the directory rather than as a habit.
 *
 * A sanitiser would be the wrong instrument here even if it were correct: it
 * implies the goal is safe HTML, and the goal is that this is not HTML. The
 * only thing a policy body is allowed to do to this page is be read.
 *
 * ===========================================================================
 * ACKNOWLEDGEMENT HAS FIVE ANSWERS, NOT TWO
 * ===========================================================================
 * See `articlesLogic.ackState`. "You have not acknowledged this", "there is
 * nothing to acknowledge", "we may not tell you" and "we could not check" are
 * four different sentences, and a `boolean` renders all four as the first one.
 *
 * ===========================================================================
 * THE LEDGER IS A COMPLIANCE SURFACE
 * ===========================================================================
 * `GET /knowledge/versions/:id/acks` is policy-attestation evidence: who
 * accepted this exact wording, and when. It is gated on `knowledge:manage` —
 * NOT on `knowledge:publish`, and not on any compliance key — which is the live
 * gate and therefore the one this screen renders against. `ledgerState` keeps
 * "nobody has signed" apart from "we never asked", because an attestation
 * report that prints a zero it did not measure is worse than no report.
 */

/** Reading a policy. Seeded to the worker role, for read plus acknowledgement. */
export const ARTICLE_READ_KEY = 'knowledge:read';
/** Writing one, and writing a new version of one. */
export const ARTICLE_WRITE_KEY = 'knowledge:write';
/** Making a version the thing readers are served. */
export const ARTICLE_PUBLISH_KEY = 'knowledge:publish';
/** Reading WHO acknowledged, and withdrawing an article. */
export const ARTICLE_MANAGE_KEY = 'knowledge:manage';

export interface ArticleDetailProps {
  articleId: string;
  /** Rendered as a "back to the list" control when the detail is opened inline. */
  onBack?: () => void;
}

/** The route form: reads `$articleId` from the path. */
export function ArticleDetailRoute() {
  const params = useParams({ strict: false }) as { articleId?: string };
  return <ArticleDetail articleId={params.articleId ?? ''} />;
}

export function ArticleDetail({ articleId, onBack }: ArticleDetailProps) {
  const { has } = usePermissions();
  const mayWrite = has(ARTICLE_WRITE_KEY);
  const mayPublish = has(ARTICLE_PUBLISH_KEY);
  const mayManage = has(ARTICLE_MANAGE_KEY);
  const queryClient = useQueryClient();

  const article = useQuery({
    queryKey: ['knowledge', 'article', articleId],
    queryFn: () => knowledge.article(articleId),
    enabled: articleId !== '',
  });

  const versions = useQuery({
    queryKey: ['knowledge', 'versions', articleId],
    queryFn: () => knowledge.versions(articleId),
    enabled: articleId !== '',
  });

  const currentVersionId = article.data?.currentVersionId ?? null;

  const acknowledged = useQuery({
    queryKey: ['knowledge', 'ack', currentVersionId],
    queryFn: () => knowledge.hasAcknowledged(currentVersionId as string),
    enabled: currentVersionId !== null,
  });

  const acks = useQuery({
    queryKey: ['knowledge', 'acks', currentVersionId],
    queryFn: () => knowledge.acks(currentVersionId as string),
    // NEVER FIRE A READ YOU KNOW IS REFUSED. `:manage` is the route's gate; a
    // refusal we can predict is one to explain, not to provoke.
    enabled: currentVersionId !== null && mayManage,
  });

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const rows = useMemo(() => versions.data ?? [], [versions.data]);
  const currentVersion = rows.find((v) => v.id === currentVersionId) ?? null;
  const shown = rows.find((v) => v.id === selectedVersionId) ?? currentVersion ?? rows[0] ?? null;

  const acknowledgeMutation = useMutation({
    mutationFn: () => knowledge.acknowledge(currentVersionId as string),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge', 'ack', currentVersionId] });
      void queryClient.invalidateQueries({ queryKey: ['knowledge', 'acks', currentVersionId] });
    },
  });

  if (articleId === '') return null;
  if (article.isLoading) return <SkeletonSet surface="drawer" rows={5} />;
  if (article.isError) {
    return (
      <LoadFailure
        error={article.error}
        mainRead
        // A policy outside the reader's programs or audience answers 404, not
        // 403 — a 403 would confirm a draft exists. So the retryable sentence
        // must not promise the policy is there.
        message="We couldn’t open this policy just now. If it is aimed at a program or a role that isn’t yours, it will not open for you at all."
        onRetry={() => void article.refetch()}
        refusedHeading="This policy is not shown to you"
      />
    );
  }

  const data = article.data;
  if (!data) return null;

  const ack = ackState(
    currentVersionId,
    acknowledged.data,
    acknowledged.isError && loadFailureKind(acknowledged.error) === 'not-allowed',
    acknowledged.isError && loadFailureKind(acknowledged.error) !== 'not-allowed',
  );

  const ledger = ledgerState({
    mayRead: mayManage,
    currentVersionId,
    acks: acks.data,
    isError: acks.isError,
    refused: acks.isError && loadFailureKind(acks.error) === 'not-allowed',
  });

  const draftNote = unpublishedDraftNote(rows, currentVersionId);

  return (
    <article className="rn-col">
      <header className="rn-card">
        {onBack ? (
          <p>
            <Button variant="quiet" small onClick={onBack}>
              ← All policies
            </Button>
          </p>
        ) : null}
        <h1 className="rn-card-title">{data.title}</h1>
        <p className="rn-row">
          <Pill variant={articleStatusVariant(data.status)}>{articleStatusLabel(data.status)}</Pill>
        </p>
        <p className="rn-muted">{articleStatusSentence(data.status)}</p>
        <p className="rn-muted">Who it is for: {audienceSentence(data)}</p>
      </header>

      {/* ------------------------------------------------------------------
          THE POLICY ITSELF
          ------------------------------------------------------------------ */}
      <section className="rn-card">
        <h2 className="rn-card-title">
          {shown === null
            ? 'The policy'
            : shown.id === currentVersionId
              ? 'The policy your people are served'
              : `Version ${shown.versionNumber}`}
        </h2>

        {versions.isError ? (
          <LoadFailure
            error={versions.error}
            message="We couldn’t load the words of this policy just now. This is not a statement that it is blank."
            onRetry={() => void versions.refetch()}
            refusedHeading="The wording is not shown to you"
          />
        ) : versions.isLoading ? (
          <SkeletonSet surface="drawer" rows={4} />
        ) : shown === null ? (
          <p className="rn-muted">
            This policy has no version yet, so there are no words to show. That is not the same as
            an empty policy.
          </p>
        ) : (
          <>
            {shown.id !== currentVersionId ? (
              <p className="rn-muted" role="note">
                {versionStandingLabel(
                  versionStanding(shown, currentVersionId, currentVersion?.versionNumber ?? null),
                )}
                . Nobody is being served these words.
              </p>
            ) : null}
            {/*
              THE UNTRUSTED BODY. Plain text into a paragraph — React escapes
              it, `pre-wrap` keeps the author's line breaks, and nothing here
              interprets the content. A handbook may contain the word "ignore",
              may quote a statute, may contain a paragraph addressed to a
              machine; all of it is rendered as characters and none of it is
              rendered as markup or obeyed.
            */}
            <p className="lib-body">{shown.body}</p>
            <p className="rn-muted">
              Written {formatInstantDay(shown.createdAt)}. The words of a version never change — an
              edit is a new version, so the wording somebody acknowledged stays readable exactly as
              they read it.
            </p>
          </>
        )}
      </section>

      {/* ------------------------------------------------------------------
          ACKNOWLEDGEMENT — the reader's own
          ------------------------------------------------------------------ */}
      <section className="rn-card">
        <h2 className="rn-card-title">Your acknowledgement</h2>
        <p className="rn-strong">{ack.label}</p>
        <p className="rn-muted">{ack.detail}</p>
        {ack.kind === 'outstanding' ? (
          <>
            <Button
              variant="primary"
              disabled={acknowledgeMutation.isPending}
              onClick={() => acknowledgeMutation.mutate()}
            >
              {acknowledgeMutation.isPending ? 'Recording…' : 'I have read this'}
            </Button>
            {acknowledgeMutation.isError ? (
              <LoadFailure
                error={acknowledgeMutation.error}
                message="We couldn’t record your acknowledgement just now. Nothing was saved, so try again."
                onRetry={() => acknowledgeMutation.mutate()}
                refusedHeading="You cannot acknowledge this"
              />
            ) : null}
          </>
        ) : null}
      </section>

      {/* ------------------------------------------------------------------
          THE LEDGER — policy-attestation evidence
          ------------------------------------------------------------------ */}
      <section className="rn-card">
        <h2 className="rn-card-title">Who has acknowledged this version</h2>
        <p className="rn-strong">{ledger.label}</p>
        <p className="rn-muted">{ledger.detail}</p>
        {ledger.kind === 'some' ? (
          <ul className="rn-list">
            {(acks.data ?? []).map((a) => (
              <li key={a.id}>
                <AckRow ack={a} />
              </li>
            ))}
          </ul>
        ) : null}
        {ledger.kind === 'failed' ? (
          <Button variant="quiet" small onClick={() => void acks.refetch()}>
            Try again
          </Button>
        ) : null}
      </section>

      {/* ------------------------------------------------------------------
          VERSIONS AND PUBLISHING — authors only
          ------------------------------------------------------------------ */}
      {mayWrite || mayPublish ? (
        <Authoring
          articleId={articleId}
          currentVersionId={currentVersionId}
          currentVersionNumber={currentVersion?.versionNumber ?? null}
          versions={rows}
          mayWrite={mayWrite}
          mayPublish={mayPublish}
          draftNote={draftNote}
          onSelect={setSelectedVersionId}
        />
      ) : null}
    </article>
  );
}

/**
 * One acknowledgement.
 *
 * THE USER ID IS NOT RENDERED. A raw `app_user` uuid on a compliance page is a
 * primary key wearing the costume of an answer, and this app already has
 * `PersonChip` / `nameResolver` for turning an id into a name where the reader
 * independently may. Until this panel is wired to one, it says the record
 * exists and when, and names the person only through the directory read a
 * future revision adds — never by printing the identifier.
 */
function AckRow({ ack }: { ack: knowledge.KnowledgeAck }) {
  return (
    <>
      <span className="rn-strong">Acknowledged {formatInstantDay(ack.ackedAt)}</span>
      <span className="rn-muted">
        {' '}
        — Phibian records who, and this screen does not yet resolve the name. That is “not shown
        here”, not “anonymous”.
      </span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Authoring
// ---------------------------------------------------------------------------

function Authoring({
  articleId,
  currentVersionId,
  currentVersionNumber,
  versions,
  mayWrite,
  mayPublish,
  draftNote,
  onSelect,
}: {
  articleId: string;
  currentVersionId: string | null;
  currentVersionNumber: number | null;
  versions: readonly knowledge.KnowledgeVersion[];
  mayWrite: boolean;
  mayPublish: boolean;
  draftNote: string | null;
  onSelect: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [warning, setWarning] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['knowledge', 'versions', articleId] });
    void queryClient.invalidateQueries({ queryKey: ['knowledge', 'article', articleId] });
    void queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
  };

  const addVersion = useMutation({
    mutationFn: (text: string) => knowledge.addVersion(articleId, text),
    onSuccess: () => {
      setBody('');
      invalidate();
    },
  });

  const publish = useMutation({
    mutationFn: (versionId: string) => knowledge.publish(articleId, versionId),
    onSuccess: (result) => {
      // A SURFACE MUST RENDER A NON-NULL WARNING AT THE POINT OF THE WRITE.
      // `AUDIENCE_MATCHES_NOBODY` is a warning on a SUCCESS: the article was
      // published and no role in this organization uses its audience band, so
      // it reaches nobody. The status word cannot carry that — after publishing
      // it reads "published" everywhere — and this is the last chance to say it.
      setWarning(result.audienceWarning);
      invalidate();
    },
  });

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Versions</h2>
      {draftNote ? (
        <p className="rn-muted" role="note">
          {draftNote}
        </p>
      ) : null}

      {versions.length === 0 ? (
        <p className="rn-muted">No version has been written yet.</p>
      ) : (
        <ol className="rn-list">
          {versions.map((v) => {
            const standing = versionStanding(v, currentVersionId, currentVersionNumber);
            return (
              <li key={v.id}>
                <span className={standing === 'published' ? 'rn-strong' : undefined}>
                  Version {v.versionNumber}
                </span>{' '}
                <span className="rn-muted">
                  — {versionStandingLabel(standing)} · written {formatInstantDay(v.createdAt)}
                </span>{' '}
                <Button variant="quiet" small onClick={() => onSelect(v.id)}>
                  Read this version
                </Button>
                {mayPublish && standing !== 'published' ? (
                  <>
                    {' '}
                    <Button
                      variant="secondary"
                      small
                      disabled={publish.isPending}
                      onClick={() => publish.mutate(v.id)}
                    >
                      Publish this version
                    </Button>
                  </>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {mayPublish ? (
        <p className="rn-muted">
          Publishing a different version moves what readers are served — and acknowledgements do not
          carry forward. Everyone who must attest to this policy will be asked again, because they
          would be signing for different words.
        </p>
      ) : (
        <p className="rn-muted">
          You can write a new version. Making one the version your people are served needs a
          separate permission.
        </p>
      )}

      {warning ? (
        <p className="rn-strong" role="alert">
          {warning}
        </p>
      ) : null}

      {publish.isError ? (
        <LoadFailure
          error={publish.error}
          message="We couldn’t publish that version just now. Nothing changed for your readers."
          refusedHeading="You cannot publish this"
        />
      ) : null}

      {mayWrite ? (
        <form
          className="rn-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim() === '') return;
            addVersion.mutate(body);
          }}
        >
          <label className="rn-col">
            <span className="rn-strong">Write a new version</span>
            <textarea
              value={body}
              rows={8}
              onChange={(e) => setBody(e.target.value)}
              aria-label="New version of this policy"
            />
          </label>
          <p className="rn-muted">
            Writing a version does not publish it. Your readers keep seeing the published one until
            somebody publishes this.
          </p>
          <Button type="submit" variant="secondary" disabled={addVersion.isPending}>
            {addVersion.isPending ? 'Saving…' : 'Save as a new version'}
          </Button>
          {addVersion.isError ? (
            <LoadFailure
              error={addVersion.error}
              message="We couldn’t save that version just now. Nothing was written, so your words are still in the box."
              refusedHeading="You cannot write versions of this policy"
            />
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
